import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect } from "react";
import {
  TextB, TextItalic, TextStrikethrough, ListBullets, ListNumbers,
  TextHOne, TextHTwo, Quotes, ArrowUUpLeft, ArrowUUpRight,
} from "@phosphor-icons/react";

function ToolbarBtn({ active, onClick, label, children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-label={label}
      className={`p-2 rounded-md transition-colors ${
        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
      }`}
    >
      {children}
    </button>
  );
}

export default function RichEditor({ value, onChange, placeholder = "What happened today?", autoFocus = false }) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose-diary min-h-[200px] outline-none",
        "data-testid": "rich-editor-content",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Treat empty <p></p> as blank
      onChange?.(html === "<p></p>" ? "" : html);
    },
    autofocus: autoFocus,
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (incoming && incoming !== current) {
      editor.commands.setContent(incoming, false);
    }
  }, [editor, value]);

  if (!editor) return null;

  return (
    <div className="border border-border rounded-lg bg-transparent">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border" data-testid="rich-toolbar">
        <ToolbarBtn testId="rt-h1" label="Heading 1"
          active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <TextHOne size={16} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn testId="rt-h2" label="Heading 2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <TextHTwo size={16} weight="bold" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn testId="rt-bold" label="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}>
          <TextB size={16} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn testId="rt-italic" label="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}>
          <TextItalic size={16} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn testId="rt-strike" label="Strike"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}>
          <TextStrikethrough size={16} weight="bold" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn testId="rt-ul" label="Bullet list"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <ListBullets size={16} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn testId="rt-ol" label="Numbered list"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListNumbers size={16} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn testId="rt-quote" label="Quote"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quotes size={16} weight="bold" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn testId="rt-undo" label="Undo"
          onClick={() => editor.chain().focus().undo().run()}>
          <ArrowUUpLeft size={16} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn testId="rt-redo" label="Redo"
          onClick={() => editor.chain().focus().redo().run()}>
          <ArrowUUpRight size={16} weight="bold" />
        </ToolbarBtn>
      </div>
      <div className="p-4">
        {editor.isEmpty && (
          <div className="pointer-events-none text-muted-foreground -mb-6 select-none">
            {placeholder}
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
